/**
 * TanStack Query hooks for Journal service
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createJournalEntry,
  getJournalEntry,
  getJournalEntries,
  updateJournalEntry,
  deleteJournalEntry,
  searchJournalEntriesByTag,
  type CreateJournalEntryInput,
  type UpdateJournalEntryInput,
} from '@/lib/services/journal';

// Query keys
export const journalKeys = {
  all: ['journal'] as const,
  entries: () => [...journalKeys.all, 'entries'] as const,
  detail: (id: string) => [...journalKeys.all, 'detail', id] as const,
  search: (tag: string) => [...journalKeys.all, 'search', tag] as const,
};

/**
 * Get all journal entries (metadata only)
 */
export function useJournalEntries(limit = 50) {
  return useQuery({
    queryKey: [...journalKeys.entries(), limit],
    queryFn: () => getJournalEntries(limit),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Get journal entry by ID (decrypted)
 */
export function useJournalEntry(entryId: string | null) {
  return useQuery({
    queryKey: journalKeys.detail(entryId || ''),
    queryFn: () => (entryId ? getJournalEntry(entryId) : null),
    enabled: !!entryId,
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Search journal entries by tag
 */
export function useJournalEntriesByTag(tag: string | null) {
  return useQuery({
    queryKey: journalKeys.search(tag || ''),
    queryFn: () => (tag ? searchJournalEntriesByTag(tag) : []),
    enabled: !!tag,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Create journal entry mutation
 */
export function useCreateJournalEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateJournalEntryInput) => createJournalEntry(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journalKeys.all });
    },
  });
}

/**
 * Update journal entry mutation
 */
export function useUpdateJournalEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entryId, input }: { entryId: string; input: UpdateJournalEntryInput }) =>
      updateJournalEntry(entryId, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: journalKeys.all });
      queryClient.setQueryData(journalKeys.detail(data.id), data);
    },
  });
}

/**
 * Delete journal entry mutation
 */
export function useDeleteJournalEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (entryId: string) => deleteJournalEntry(entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journalKeys.all });
    },
  });
}




